'use client';

import React, { useEffect, Suspense } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { Header } from '@/components/layout/header';
import { Footer } from '@/components/layout/footer';
import { CookieConsentBanner } from '@/components/cookie-consent-banner';
import { useAuth } from '@/context/auth-context';
import { recordPathname } from '@/lib/navigation-history';
import { isStorefrontPath } from '@/lib/storefront-url';
import { isDecodablePath } from '@/lib/path-decoding';
import { captureLinkDiscountCode } from '@/lib/discount-code-link';
import { PreferencesConfirmDialog } from '@/components/preferences-confirm-dialog';
import { CartFlightLayer } from '@/components/cart-flight-layer';
import { DotGrid } from '@/components/ui/dot-grid';
import { InstalledAppNotice } from '@/components/installed-app-notice';
import { useCosmicEffects } from '@/hooks/use-cosmic-effects';
import type { GradientDirection } from '@/lib/background-prefs';

/**
 * The starfield and planets, fetched on demand: a chunk of about 27 KB
 * minified (11 KB gzipped, measured 2026-10-21) that checkout, light mode,
 * reduced motion and every plain seller page never draw. The gate below renders it only after hydration, once stars or
 * planets are on, so there is no server markup for it to match (`ssr: false`),
 * and the first page that needs it fetches it then. A static import of the
 * sky's modules anywhere else puts them back in this bundle
 * (src/lib/sky-bundle.test.ts). Light mode's dot grid is a CSS background
 * and comes with this bundle; the part of it that moves loads on its own
 * (src/components/ui/dot-grid.tsx).
 */
const Starfield = dynamic(() => import('@/components/ui/starfield'), { ssr: false });

/**
 * The background gradient, one whole class string per direction so Tailwind's
 * scanner sees every one (a class assembled at runtime is never generated).
 * `gradient` is the --gradient colour ThemeProvider sets. Dark mode only (owner
 * decision 2026-09-30); the gate never turns it on in light mode.
 */
const GRADIENT_CLASS: Record<GradientDirection, string> = {
    bottom: 'bg-gradient-to-b from-black to-gradient/20',
    top: 'bg-gradient-to-t from-black to-gradient/20',
};

/**
 * The same gradient on a seller's own background colour: it rises from that
 * colour (`--background`, which ThemeProvider sets to it) instead of from
 * black, or the layer would paint over the colour the seller picked. Kept apart
 * from the classes above so every other page keeps its exact black.
 */
const CUSTOM_BACKGROUND_GRADIENT_CLASS: Record<GradientDirection, string> = {
    bottom: 'bg-gradient-to-b from-background to-gradient/20',
    top: 'bg-gradient-to-t from-background to-gradient/20',
};


function LayoutRenderer({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const { isAuthenticated, user } = useAuth();
    // Which background effects render here: the viewer's toggles, a seller's
    // page override, the theme, reduced motion and the checkout rule, all
    // decided in one place (src/lib/cosmic-effects.ts). Everything is off
    // until after mount, so SSR (which cannot read `prefers-color-scheme`)
    // and the first client render produce identical markup; without that,
    // dark-OS users hydrated with an extra gradient/star child and React threw
    // the subtree away. It also stays off until the viewer's own settings are
    // in, so an effect they switched off never flashes on the new defaults.
    const fx = useCosmicEffects();

    useEffect(() => {
        recordPathname(pathname);
    }, [pathname]);

    // A seller's discount code arriving in a link (?code=SAVE10) is kept for
    // this tab, so checkout can apply it however the buyer gets there: the
    // store, a product page, a payment link, then the cart. Read from the
    // address bar in an effect rather than through useSearchParams, which would
    // put every page this layout wraps behind a Suspense boundary. Only a valid
    // code is kept, and the server decides whether it applies. See
    // src/lib/discount-code-link.ts.
    useEffect(() => {
        captureLinkDiscountCode(window.location.search);
    }, [pathname]);

    // A seller's storefront supplies its OWN nav, <main> and footer, from
    // src/app/profile/[username]/layout.tsx, so the site chrome steps aside
    // here. Three reasons it is done this way rather than by swapping <Header />
    // for a storefront header in this file:
    //
    //   1. This is a client component and the seller's name, avatar and links
    //      live on a document it never reads. The route's own layout is a SERVER
    //      component that already has them from a cache()d read, so rendering
    //      there puts the nav in the HTML we serve instead of behind a 300ms
    //      debounce and a Firestore round trip.
    //   2. The storefront nav has to be a SIBLING of <main>, not the first thing
    //      inside it, or the skip link below lands the visitor on the nav it
    //      exists to skip.
    //   3. The storefront nav and footer reproduce the Header's row insets
    //      (STOREFRONT_ROW_INSET in src/components/storefront/storefront-nav.tsx)
    //      so the seller's name sits where the logo sits and the cart where the
    //      account control sits. That arithmetic holds only for a direct child
    //      of the column below, which is what the Header is, and the route's
    //      layout renders them there.
    //
    // The storefront's <main> is NOT a seller option. It carries this file's
    // own `container mx-auto px-4` column (the full-width choice was retired
    // on 2026-09-28), and profile-page-render.test.ts compares the two <main>
    // classNames whole, so edit them together.
    //
    // THE GLOBAL FOOTER IS DROPPED HERE TOO, and with it the business
    // identification and the legal links: a storefront's footer is "Powered by
    // supa.place" and the display currency only, by owner decision
    // (2026-09-28). What makes that acceptable is that isStorefrontPath is true
    // for a storefront and nothing else, so /cart, /checkout, /buy, every
    // product page and every other route still render the global Footer
    // below, where the contract is formed. storefront-bar.test.ts pins both
    // halves (and reads the JSX, so do not quote the element in a comment).
    //
    // isStorefrontPath matches BOTH /@name and /profile/name on purpose: the
    // public URL is a rewrite over the internal route, and which of the two
    // usePathname reports is not something this file should depend on. Both
    // return true, so server and client render the same tree either way and
    // there is no hydration mismatch. usePathname is resolved during SSR, so
    // this branch is not gated on mount the way the background effects are.
    // A storefront path that cannot be decoded (/@caf%E9) is rewritten to the
    // site 404 by middleware, which has no storefront layout to draw the chrome,
    // so the site's header, main and footer stay for it.
    const isStorefront = isStorefrontPath(pathname) && isDecodablePath(pathname ?? '');

    return (
        <div className="relative min-h-screen flex flex-col">
            {/* Every layer sits in the root stacking context at negative z,
                under the z-10 content wrapper and over the body background.
                The gradient and the dot grid span the whole document height,
                not the viewport; one is dark mode's and the other light
                mode's, so they never meet. */}
            {fx.grid && <DotGrid />}
            {fx.gradient && (
                <div
                    aria-hidden
                    className={`pointer-events-none absolute top-0 left-0 w-full h-full -z-20 ${(fx.customBackground ? CUSTOM_BACKGROUND_GRADIENT_CLASS : GRADIENT_CLASS)[fx.gradientDirection]}`}
                />
            )}
            {(fx.stars || fx.planets) && <Starfield stars={fx.stars} planets={fx.planets} theme={fx.theme} />}
            <div className="relative z-10 flex flex-col min-h-screen">
                {/* Bypass block (WCAG 2.4.1, Level A). The header carries the
                    whole nav, the search field, the cart and the account menu,
                    and without this a keyboard or switch user re-tabs every one
                    of them on every navigation before reaching the page.

                    FIRST focusable element in the document, which is the only
                    position that works: it has to be reachable on the very first
                    Tab. Hidden by being positioned off-screen rather than by
                    `display: none` or `hidden`, because either of those takes it
                    out of the tab order and it would never be reachable at all;
                    `focus:` brings it back into view for the one keystroke it is
                    needed. It stays invisible to a pointer user throughout.

                    The target is the <main> below. Giving that element an id is
                    the whole other half: the link had nothing to point at.

                    KEPT ON THE STOREFRONT TOO. The global header is gone there,
                    but the seller's own nav is still a block repeated across
                    every page of their store, which is exactly what a bypass
                    block is for. The storefront's layout renders the matching
                    #main-content, so the target still exists. */}
                <a
                    href="#main-content"
                    className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
                >
                    Skip to main content
                </a>
                {/* Above both the site header and a storefront's own nav, so an
                    installed copy shows it on every route. Renders nothing in
                    a normal browser tab. */}
                <InstalledAppNotice />
                {isStorefront ? (
                    children
                ) : (
                    <>
                        <Header />
                        {/* tabIndex={-1} so the skip link can actually MOVE focus here.
                            Without it the browser scrolls to the element and leaves
                            focus on the link, so the next Tab goes back into the header
                            and the link does nothing for the users it exists for. */}
                        <main id="main-content" tabIndex={-1} className="flex-grow container mx-auto px-4 flex focus:outline-none">
                            {children}
                        </main>
                        <Footer />
                    </>
                )}
            </div>
            {/* The add-to-cart shooting star. Outside the z-10 wrapper so its
                fixed canvas can sit over the header it flies to. Gated on the
                starfield because the star belongs to that sky. */}
            <CartFlightLayer enabled={fx.cartFlight} theme={fx.theme} />
            <CookieConsentBanner />
            {/* Self-gates: renders only for a brand-new account that hasn't yet
                confirmed its auto-detected regional settings. */}
            <PreferencesConfirmDialog />
        </div>
    );
}


export function ClientLayout({ children }: { children: React.ReactNode }) {
  // The useEffect to apply the theme class has been removed from here
  // and is now handled solely within the ThemeProvider.
  return <LayoutRenderer>{children}</LayoutRenderer>
}
